import React, { useEffect, useState, useRef } from "react";
import Stepper from "./stepper";
import axios from "axios";
import env from "../../../config/index";
import { useUsersContext } from "context/usersContext";
import Icon from "components/Icon";
import Nango from "@nangohq/frontend";
import microsoftteamIcon from "../../../assets/images/MicrosoftTeams.png";

const CommonModal = ({ isOpen, onClose, children }) => {
  return (
    <>
      {isOpen && (
        <div className="modal-overlay" onClick={onClose}>
          <div
            className="modal-content-1 text-center"
            style={{ minWidth: "560px", position: "relative" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                position: "absolute",
                top: "15px",
                right: "15px",
                cursor: "pointer",
                zIndex: 100,
              }}
              onClick={onClose}
            >
              <Icon id="close" width={14} height={14} />
            </div>
            {children}
          </div>
        </div>
      )}
    </>
  );
};

const IntegrationModal = ({
  isOpen,
  onClose,
  requiredIntegrations,
  onAllConnected,
  rqIntegration,
  integration_id
}) => {
  const { SSO } = useUsersContext();
  const [connectedInt, setConnectedInt] = useState([]);
  const [waiting, setWaiting] = useState(false);
  const [loading, setLoading] = useState(false);
  const BASE_URL = `${env.API_URL}/v1`;
  const integrationsToShow = requiredIntegrations || [];
  const { agencyInfo } = useUsersContext()
  // Use useRef to persist the interval reference across re-renders
  const connectionIntervalRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const NANGO_API_HOST = env.NANGO_API_HOST || "https://api.nango.dev";
  const NANGO_CONNECT_UI_HOST = env.NANGO_CONNECT_UI_HOST || "https://connect.nango.dev";
  const hasConnectedRef = useRef(false);

  const nango = new Nango({
    host: NANGO_API_HOST,
    apiURL: NANGO_API_HOST
  });

  const handleInstallApp = (provider) => {
    setLoading(true);
    let installUrl = "";
    if (provider === "microsoft-teams") {
      installUrl = process.env.REACT_APP_TEAMS_URL;
    }
    window.open(installUrl, "_blank");
    setLoading(false);
  }

  useEffect(() => {
    if (isOpen) {
    } else {
      // Reset loading state when modal is closed
      setLoading(false);
      if (connectionIntervalRef.current) {
        clearInterval(connectionIntervalRef.current);
        connectionIntervalRef.current = null;
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    }
  }, []);

  const checkConnection = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/prompts/providers`, {
        params: {
          profile_id: SSO?.id,
          user_id: SSO?.UserInfo?.userId,
        },
        timeout: 10000,
      });

      if (res?.data?.data?.data) {
        // Clear both interval and timeout
        if (connectionIntervalRef.current) {
          clearInterval(connectionIntervalRef.current);
          connectionIntervalRef.current = null;
        }
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        setLoading(null);
      }
    } catch (err) {
      console.error("Connection check failed", err);
    }
  };


  const handleClose = () => {
    onClose();
  };

  const launchOAuthPopup = async (provider) => {
    try {
      const sessionResponse = await axios.post(
        `${BASE_URL}/integrations/nango/createConnectSession`,
        {
          end_user: {
            id: SSO?.UserInfo?.userId,
            email: SSO?.UserInfo?.email,
            display_name: SSO?.UserInfo?.userName || "User",
          },
          allowed_integrations: [provider],
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!sessionResponse.data?.success || !sessionResponse.data?.data?.token) {
        throw new Error(
          sessionResponse.data?.message ||
          sessionResponse.data?.error ||
          "Failed to get session token"
        );
      }

      const sessionToken = sessionResponse.data.data.token;
      const connect = nango.openConnectUI({
        baseURL: NANGO_CONNECT_UI_HOST,
        apiURL: NANGO_API_HOST,
        onEvent: async (event) => {
          if (event.type === "close") {
          } else if (event.type === "connect") {
            try {
              const { connectionId } = event.payload;
              const sendConnectionToBackend = await axios.post(`${BASE_URL}/integrations/nango/storeSession`, { provider, connection_id: connectionId, profile_id: SSO?.id, user_id: SSO?.UserInfo?.userId, agency_id: agencyInfo?._id, ...(integration_id ? { integration_id } : {}) });
              if (sendConnectionToBackend.data.success) {
                hasConnectedRef.current = true;
                onAllConnected()
                onClose()
              }
              else {
              }
            } catch (err) {
            }
          }
        },
      });

      connect.setSessionToken(sessionToken);
    } catch (err) {
      console.error("Nango connection error:", err);
    }
  };


  return (
    <CommonModal
      title=""
      isOpen={isOpen}
      onClose={handleClose}
      showAlert={false}
    >
      <div style={{ padding: '40px 30px', position: 'relative', maxHeight: '85vh', overflowY: 'auto', background: 'white', width: '580px' }}>
        {waiting && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.9)', borderRadius: 15, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            {/* small spinner */}
            <style>{`@keyframes im-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #ccc', borderTopColor: '#000', marginBottom: 12, animation: 'im-spin 1s linear infinite' }}></div>
          </div>
        )}

        <h1 style={{ textAlign: 'center', fontSize: 16, fontWeight: 600, margin: 0 }}>
          {`Connect to ${rqIntegration === 'slack' ? "Slack" : "Microsoft Teams"}`}
        </h1>
        <p style={{ textAlign: 'center', fontSize: 13, marginTop: 8 }}>
          {rqIntegration === 'slack' ? 'To create this task, connect to your Slack Workplace' : "To create this task, first follow the install app instructions and then you will be able to connect to Microsoft Teams."}
        </p>

        <div style={{ marginTop: 16 }}>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, border: '1px solid #e6e6e6', padding: '7px 10px', borderRadius: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {rqIntegration === 'slack' ? (
                <Icon id="slack" width={30} height={30} />
              ) : (
                <img
                  src={microsoftteamIcon}
                  alt={rqIntegration}
                  style={{ width: 40, height: 40 }}
                />
              )}
              < p style={{ fontSize: 14, fontWeight: 600, margin: 0, paddingLeft: 10 }}>{rqIntegration === 'slack' ? "Slack" : "Microsoft Teams"}</p>
            </div>

            {rqIntegration === 'microsoft-teams' && <button
              style={{ background: '#1E1E1E', color: '#fff', fontSize: 14, borderRadius: 3, width: '134px', height: '42px', border: 'none', cursor: loading ? 'default' : 'pointer', fontWeight: '600' }}
              onClick={() => handleInstallApp('microsoft-teams')}
              disabled={loading}
            >
              {loading ? `Installing...` : `Install App`}
            </button>}

            <button
              style={rqIntegration === 'slack' ? { background: '#1E1E1E', color: '#fff', fontSize: 14, borderRadius: 3, width: '134px', height: '42px', border: 'none', cursor: loading ? 'default' : 'pointer', fontWeight: '600' } : { background: '#fff', color: '#1E1E1E', fontSize: 14, borderRadius: 3, width: '134px', height: '42px', border: '1px solid #1E1E1E', cursor: loading ? 'default' : 'pointer', fontWeight: '600' }}
              onClick={() => launchOAuthPopup(rqIntegration)}
              disabled={loading}
            >
              {loading ? `Connecting...` : `Connect`}
            </button>
          </div>
        </div>

        {/* <button
          style={{ width: '100%', marginTop: "10px", padding: '12px 0', borderRadius: 3, textAlign: 'center', fontWeight: 600, fontSize: 16, background: "#1E1E1E", color: 'white', border: 'none', cursor: 'pointer' }}
          onClick={() => {
            onAllConnected?.();
            handleClose();
          }}
        >
          Finish Task Set Up
        </button> */}
      </div>

    </CommonModal >
  );
};

export default IntegrationModal;
